<template>
  <div class="AnsibleStageView">
    <section class="AnsibleStageView__metrics">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="AnsibleStageView__metric"
        :class="`is-${metric.variant}`"
      >
        <span class="zp-overline">{{ metric.label }}</span>
        <strong class="zp-tabular">{{ metric.value }}</strong>
        <small>{{ metric.note }}</small>
      </div>
    </section>

    <section class="AnsibleStageView__main">
      <div class="AnsibleStageView__panel AnsibleStageView__panel--wide">
        <div class="AnsibleStageView__panel-head">
          <div>
            <span class="zp-overline">{{ $t('stageAnalysis') }}</span>
            <h3>{{ $t('tabSummary') }}</h3>
          </div>
        </div>
        <div class="AnsibleStageView__table-wrap">
          <table class="AnsibleStageView__table">
            <thead>
              <tr>
                <th>{{ $t('stage') }}</th>
                <th>{{ $t('status') }}</th>
                <th>{{ $t('duration') }}</th>
                <th>{{ $t('hosts') }}</th>
                <th>OK</th>
                <th>{{ $t('changed') }}</th>
                <th>{{ $t('failed') }}</th>
                <th>{{ $t('duration') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="stage in stageRows" :key="stage.key">
                <td>
                  <strong>{{ stage.name }}</strong>
                  <small>{{ stage.note }}</small>
                </td>
                <td><span :class="`AnsibleStageView__status is-${stage.variant}`"></span></td>
                <td class="zp-tabular">{{ stage.duration }}</td>
                <td class="zp-tabular">{{ stage.hosts }}</td>
                <td class="zp-tabular is-ok">{{ stage.ok }}</td>
                <td class="zp-tabular">{{ stage.changed }}</td>
                <td class="zp-tabular is-failed">{{ stage.failed }}</td>
                <td>
                  <span class="AnsibleStageView__bar">
                    <span :style="{ width: `${stage.percent}%` }"></span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="AnsibleStageView__panel">
        <div class="AnsibleStageView__panel-head">
          <div>
            <span class="zp-overline">{{ $t('anomalies') }}</span>
            <h3>{{ failedTasks.length ? $t('notOkHosts') : $t('noFailures') }}</h3>
          </div>
        </div>
        <div v-if="failedTasks.length === 0" class="AnsibleStageView__empty">
          <ZpIcon name="check" :size="18" />
          {{ $t('allStagesCompleted') }}
        </div>
        <div v-else class="AnsibleStageView__failed-list">
          <div v-for="(task, index) in failedTasks.slice(0, 8)" :key="`${task.host}-${index}`">
            <strong>{{ task.host }}</strong>
            <span>{{ task.task }}</span>
            <small>{{ task.error }}</small>
          </div>
        </div>
      </div>
    </section>

    <section class="AnsibleStageView__hosts">
      <div class="AnsibleStageView__host-head">
        <span class="zp-overline">{{ $t('hosts') }}</span>
        <div class="AnsibleStageView__switch">
          <button
            type="button"
            :class="{ 'is-active': tab === 'notOkServers' }"
            @click="tab = 'notOkServers'"
          >
            {{ $t('notOkHosts') }}
          </button>
          <button
            type="button"
            :class="{ 'is-active': tab === 'allServers' }"
            @click="tab = 'allServers'"
          >
            {{ $t('allHosts') }}
          </button>
        </div>
      </div>

      <div v-if="tab === 'notOkServers'" class="AnsibleStageView__table-wrap">
        <table class="AnsibleStageView__table">
          <thead>
            <tr>
              <th>{{ $t('host') }}</th>
              <th>{{ $t('task') }}</th>
              <th>{{ $t('error') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="failedTasks.length === 0">
              <td colspan="3">{{ $t('noFailures') }}</td>
            </tr>
            <tr
              v-for="(task, index) in failedTasks"
              v-else
              :key="`${task.host}-${task.task}-${index}`"
            >
              <td>{{ task.host }}</td>
              <td>{{ task.task }}</td>
              <td class="is-failed">{{ task.error }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="AnsibleStageView__table-wrap">
        <table class="AnsibleStageView__table">
          <thead>
            <tr>
              <th>{{ $t('host') }}</th>
              <th>{{ $t('changed') }}</th>
              <th>{{ $t('failed') }}</th>
              <th>{{ $t('ignored') }}</th>
              <th>OK</th>
              <th>{{ $t('rescued') }}</th>
              <th>{{ $t('skipped') }}</th>
              <th>{{ $t('unreachable') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(host, index) in hosts" :key="`${host.host}-${index}`">
              <td>{{ host.host }}</td>
              <td class="zp-tabular">{{ host.changed }}</td>
              <td class="zp-tabular is-failed">{{ host.failed }}</td>
              <td class="zp-tabular">{{ host.ignored }}</td>
              <td class="zp-tabular is-ok">{{ host.ok }}</td>
              <td class="zp-tabular">{{ host.rescued }}</td>
              <td class="zp-tabular">{{ host.skipped }}</td>
              <td class="zp-tabular is-failed">{{ host.unreachable }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style lang="scss">
.AnsibleStageView {
  display: grid;
  gap: var(--zp-space-sm);

  &__metrics {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: var(--zp-space-sm);
  }

  &__metric,
  &__panel,
  &__hosts {
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
    overflow: hidden;
  }

  &__metric {
    padding: var(--zp-space-md);

    strong,
    small {
      display: block;
    }

    strong {
      margin-top: var(--zp-space-xs);
      color: var(--zp-text-primary);
      font-size: var(--zp-text-2xl);
      font-weight: 700;
      line-height: var(--zp-leading-tight);
    }

    small {
      margin-top: var(--zp-space-2xs);
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-xs);
    }

    &.is-success {
      border-color: var(--zp-status-success-bg);
    }

    &.is-danger {
      border-color: var(--zp-status-danger-bg);
    }
  }

  &__main {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(280px, 0.8fr);
    gap: var(--zp-space-sm);
  }

  &__panel-head,
  &__host-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--zp-space-sm);
    padding: var(--zp-space-sm) var(--zp-space-md);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);

    h3 {
      margin: var(--zp-space-2xs) 0 0;
      color: var(--zp-text-primary);
      font-family: var(--zp-font-display);
      font-size: var(--zp-text-md);
      font-weight: 650;
    }
  }

  &__table-wrap {
    max-width: 100%;
    overflow: auto;
  }

  &__table {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
    font-size: var(--zp-text-xs);

    th,
    td {
      padding: var(--zp-space-xs) var(--zp-space-sm);
      border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
      text-align: left;
      vertical-align: top;
    }

    th {
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-2xs);
      font-weight: 700;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
    }

    strong,
    small {
      display: block;
    }

    strong {
      color: var(--zp-text-primary);
      font-weight: 650;
    }

    small {
      color: var(--zp-text-tertiary);
    }
  }

  &__status {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: var(--zp-radius-pill);
    background: var(--zp-status-neutral);

    &.is-success {
      background: var(--zp-status-success);
    }

    &.is-danger {
      background: var(--zp-status-danger);
    }
  }

  &__bar {
    display: block;
    width: 100%;
    min-width: 86px;
    height: 5px;
    margin-top: 5px;
    border-radius: var(--zp-radius-pill);
    background: var(--zp-bg-muted);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background: var(--zp-brand);
    }
  }

  &__empty,
  &__failed-list {
    padding: var(--zp-space-md);
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: var(--zp-space-xs);
    color: var(--zp-status-success);
    font-size: var(--zp-text-sm);
    font-weight: 650;
  }

  &__failed-list {
    display: grid;
    gap: var(--zp-space-sm);

    div {
      padding-bottom: var(--zp-space-sm);
      border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    }

    strong,
    span,
    small {
      display: block;
      line-height: var(--zp-leading-snug);
    }

    strong {
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
    }

    span {
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
    }

    small {
      color: var(--zp-status-danger);
      font-size: var(--zp-text-xs);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__switch {
    display: inline-flex;
    padding: 2px;
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);

    button {
      min-height: 28px;
      padding: 0 var(--zp-space-sm);
      border: 0;
      border-radius: var(--zp-radius-sm);
      background: transparent;
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
      font-weight: 700;
      cursor: pointer;

      &.is-active {
        background: var(--zp-brand-soft);
        color: var(--zp-brand);
      }
    }
  }

  .is-ok {
    color: var(--zp-status-success);
  }

  .is-failed {
    color: var(--zp-status-danger);
  }
}

@media (max-width: 1100px) {
  .AnsibleStageView {
    &__metrics {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__main {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 700px) {
  .AnsibleStageView {
    &__metrics {
      grid-template-columns: 1fr;
    }

    &__host-head {
      align-items: stretch;
      flex-direction: column;
    }
  }
}
</style>

<script>
import ProjectMixin from '@/components/ProjectMixin';

export default {
  props: {
    projectId: Number,
    taskId: Number,
    premiumFeatures: Object,
  },

  mixins: [ProjectMixin],

  data() {
    return {
      stages: [],
      okServers: 0,
      notOkServers: 0,
      tab: 'notOkServers',
      failedTasks: [],
      hosts: [],
    };
  },

  watch: {
    async taskId() {
      await this.loadData();
      this.calcStats();
    },
  },

  computed: {
    totals() {
      return this.hosts.reduce((acc, host) => ({
        ok: acc.ok + (host.ok || 0),
        changed: acc.changed + (host.changed || 0),
        failed: acc.failed + (host.failed || 0),
        skipped: acc.skipped + (host.skipped || 0),
        unreachable: acc.unreachable + (host.unreachable || 0),
      }), {
        ok: 0,
        changed: 0,
        failed: 0,
        skipped: 0,
        unreachable: 0,
      });
    },

    metrics() {
      return [
        {
          key: 'hosts',
          label: this.$t('hosts'),
          value: this.hosts.length,
          note: `${this.notOkServers} ${this.$t('notOkHosts').toLowerCase()}`,
          variant: this.notOkServers > 0 ? 'danger' : 'success',
        },
        {
          key: 'ok',
          label: 'OK',
          value: this.totals.ok,
          note: this.$t('taskEvents'),
          variant: 'success',
        },
        {
          key: 'changed',
          label: this.$t('changed'),
          value: this.totals.changed,
          note: this.$t('taskEvents'),
          variant: 'neutral',
        },
        {
          key: 'failed',
          label: this.$t('failed'),
          value: this.totals.failed + this.totals.unreachable,
          note: this.$t('taskEvents'),
          variant: this.totals.failed + this.totals.unreachable > 0 ? 'danger' : 'success',
        },
        {
          key: 'skipped',
          label: this.$t('skipped'),
          value: this.totals.skipped,
          note: this.$t('taskEvents'),
          variant: 'neutral',
        },
      ];
    },

    stageRows() {
      const rows = (this.stages || []).map((stage, index) => ({
        key: stage.id || stage.name || index,
        name: stage.name || stage.task || `${this.$t('stage')} ${index + 1}`,
        note: stage.play || stage.description || '—',
        duration: this.formatStageDuration(stage),
        durationSeconds: this.stageSeconds(stage),
        hosts: stage.hosts || this.hosts.length || '—',
        ok: stage.ok || 0,
        changed: stage.changed || 0,
        failed: stage.failed || 0,
        variant: stage.failed > 0 ? 'danger' : 'success',
      }));

      const fallback = rows.length > 0 ? rows : [
        {
          key: 'recap',
          name: this.$t('recap'),
          note: this.$t('allStagesCompleted'),
          duration: '—',
          durationSeconds: 1,
          hosts: this.hosts.length || '—',
          ok: this.totals.ok,
          changed: this.totals.changed,
          failed: this.totals.failed + this.totals.unreachable,
          variant: this.totals.failed + this.totals.unreachable > 0 ? 'danger' : 'success',
        },
      ];

      const max = Math.max(...fallback.map((row) => row.durationSeconds || 1), 1);
      return fallback.map((row) => ({
        ...row,
        percent: Math.max(6, Math.round(((row.durationSeconds || 1) / max) * 100)),
      }));
    },
  },

  async created() {
    await this.loadData();
    this.calcStats();
  },

  methods: {
    async loadData() {
      const safe = async (path) => {
        try {
          const result = await this.loadProjectEndpoint(path);
          return Array.isArray(result) ? result : [];
        } catch (e) {
          return [];
        }
      };

      const [failedTasks, hosts, stages] = await Promise.all([
        safe(`/tasks/${this.taskId}/ansible/errors`),
        safe(`/tasks/${this.taskId}/ansible/hosts`),
        safe(`/tasks/${this.taskId}/stages`),
      ]);

      this.hosts = hosts;
      this.stages = stages;
      this.failedTasks = [...failedTasks];

      this.hosts.forEach((host) => {
        if (host.unreachable) {
          this.failedTasks.push({
            host: host.host,
            task: '—',
            error: 'Host is unreachable',
          });
        }
      });
    },

    calcStats() {
      this.okServers = 0;
      this.notOkServers = 0;
      this.hosts.forEach((host) => {
        if (host.failed > 0 || host.unreachable > 0) {
          this.notOkServers += 1;
        } else {
          this.okServers += 1;
        }
      });
    },

    stageSeconds(stage) {
      const raw = stage.duration || stage.duration_seconds || stage.elapsed || 0;
      if (typeof raw === 'number') return raw;
      const parsed = parseFloat(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    },

    formatStageDuration(stage) {
      const seconds = Math.round(this.stageSeconds(stage));
      if (!seconds) return '—';
      const minutes = Math.floor(seconds / 60);
      return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    },
  },
};
</script>
